import React, { useState, useEffect, useCallback, useRef, useMemo, useContext } from 'react';
import { Snackbar, Alert } from '@mui/material';
import api from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { useActiveCatalog } from '../hooks/useActiveCatalog';
import { CollectionsContext, PermissionsContext } from './complexContexts';
import {
  CollectionWithRole,
  CollectionCreate,
  CollectionUpdate,
  CollectionMemberResponse,
  PendingInvitation,
  CollectionItemMembership,
  ItemCollectionInfo
} from '../types/collection';
import { logger } from '../utils/logger';

const CollectionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { activeCatalogOwnerId, activeCatalogAliId, availableCatalogs, switchToCatalogAliId } =
    useActiveCatalog();
  // Read PermissionsContext directly (not the throwing usePermissions hook) so
  // CollectionsProvider degrades gracefully if rendered without the provider
  // (e.g. in isolated tests); the real app always nests it under it.
  const permissionsCtx = useContext(PermissionsContext);
  const ownUserId = permissionsCtx?.permissions?.userId ?? null;
  const [collections, setCollections] = useState<CollectionWithRole[]>([]);
  // The cross-catalog union (own + active full delegations), independent of the
  // active catalog. Drives the Albums page and the robust per-object album list.
  const [allAccessibleCollections, setAllAccessibleCollections] = useState<CollectionWithRole[]>([]);
  // Dedicated loading/error for the union fetch (the Albums page reads these),
  // kept separate from the active-catalog `isLoading`/`error` which belong to
  // `fetchCollections`. Start true so the Albums page shows a loading state on
  // first mount instead of a false "no albums yet" flash before the union lands.
  const [allAccessibleLoading, setAllAccessibleLoading] = useState(true);
  const [allAccessibleError, setAllAccessibleError] = useState<string | null>(null);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  // GH#1213: invalidation counter — bumped after every item add/remove so
  // consumers (e.g. MyObjects loadCollectionItemIds effect) re-fetch album
  // membership without a full page reload.
  const [membershipVersion, setMembershipVersion] = useState(0);

  // Track if collection selection has been initialized to avoid overwriting user's "All Items" selection
  const isInitialized = useRef(false);

  // Holds the id of an album just created in a DIFFERENT catalog than the one
  // on screen. The caller switches the active catalog right after; the
  // catalog-switch reset effect below consumes this so the freshly-created
  // album is selected after the switch instead of resetting to "All Items"
  // (matching the same-pool create, which auto-selects immediately).
  const pendingSelectAfterSwitchRef = useRef<string | null>(null);

  // Track the previous active catalog identity (owner + ALI, separately) so we
  // can detect a catalog switch synchronously within the effect. A ref (not
  // state) avoids an extra render cycle. Owner and ALI are kept apart so we can
  // tell a genuine switch from the async resolution of the owner id (#1201).
  const prevActiveCatalogRef = useRef<{ owner: string | null; ali: string | null }>({
    owner: activeCatalogOwnerId,
    ali: activeCatalogAliId,
  });

  // Reset the selected collection whenever the active catalog changes (e.g. the
  // user switches between delegated catalogs or their own add-on catalogs).
  // Without this reset, a collection selected under Catalog A would remain
  // selected when switching to Catalog B, causing only Catalog A's album items
  // to render in Catalog B's view (issue #1159 catalog-switch variant).
  //
  // #1201 narrows this: for a delegate, `activeCatalogAliId` identifies the
  // active catalog up front (hydrated from localStorage), but
  // `activeCatalogOwnerId` resolves asynchronously from null -> delegatorUserId
  // AFTER fetchDelegations. That `null -> value` transition (ALI UNCHANGED) is
  // resolution of the SAME catalog, not a switch, so it must NOT clear the
  // album the delegate just selected. We skip the reset for exactly that
  // transition. It is provably safe: an ALI id maps to one catalog with one
  // owner, so no real switch can turn a null owner into a value without also
  // changing the ALI id. Every genuine switch still resets — the unified
  // switcher changes the ALI id, and legacy switchToOwnCatalog moves the owner
  // OFF a value (value -> null), neither of which is the skipped case.
  useEffect(() => {
    const prev = prevActiveCatalogRef.current;
    const ownerChanged = prev.owner !== activeCatalogOwnerId;
    const aliChanged = prev.ali !== activeCatalogAliId;
    if (!ownerChanged && !aliChanged) return;

    // Async owner-id resolution for an unchanged catalog: skip the reset.
    const isOwnerResolution = !aliChanged && !prev.owner && !!activeCatalogOwnerId;

    prevActiveCatalogRef.current = { owner: activeCatalogOwnerId, ali: activeCatalogAliId };

    // A cross-pool create just switched us into the catalog that now owns the
    // new album: select it instead of clearing to "All Items", so cross-pool
    // create matches same-pool create (which auto-selects). Consumed BEFORE the
    // owner-resolution skip so a pending id can never linger across an
    // early-returning run and mis-fire on a later unrelated switch. We leave
    // `isInitialized` false so the init effect re-validates the id against the
    // refetched list once it loads — self-healing to "All Items" if the album
    // isn't there (e.g. read-after-write lag or a refetch error).
    const pendingSelection = pendingSelectAfterSwitchRef.current;
    if (pendingSelection) {
      pendingSelectAfterSwitchRef.current = null;
      setSelectedCollectionId(pendingSelection);
      localStorage.setItem('selectedCollectionId', pendingSelection);
      isInitialized.current = false;
      return;
    }

    if (isOwnerResolution) return;

    setSelectedCollectionId(null);
    localStorage.removeItem('selectedCollectionId');
    // Allow the initialization effect to re-run for the new catalog so any
    // saved collection ID that belongs to the new catalog can be restored.
    isInitialized.current = false;
  }, [activeCatalogOwnerId, activeCatalogAliId]);

  // One-time migration from old localStorage key
  useEffect(() => {
    const old = localStorage.getItem('selectedCatalogId');
    if (old) {
      localStorage.setItem('selectedCollectionId', old);
      localStorage.removeItem('selectedCatalogId');
    }
  }, []);

  const fetchCollections = useCallback(
    async (ownerId?: string | null, catalogAliId?: string | null) => {
      // Don't fetch if not authenticated
      if (!isAuthenticated) {
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        // PR 4b — both filters compose: ownerId for delegations, catalogAliId
        // to scope to one owned catalog. Either may be set independently.
        const params = new URLSearchParams({ limit: '100' });
        if (ownerId) params.append('owner_id', ownerId);
        if (catalogAliId) params.append('catalog_ali_id', catalogAliId);
        const url = `/api/v2/collections?${params.toString()}`;
        const response = await api.get(url);
        setCollections(response.data);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch collections';
      setError(errorMessage);
      setSnackbarMessage(`Error loading collections: ${errorMessage}`);
      setSnackbarOpen(true);
      logger.error('Error fetching collections', error);
        // DON'T throw - keep context functional
      } finally {
        setIsLoading(false);
      }
    },
    [isAuthenticated],
  );

  // Fetch the cross-catalog union: the caller's own albums plus those of every
  // catalog they hold an active FULL delegation for. Independent of the active
  // catalog so the Albums page can show all accessible albums at once, and so
  // the per-object album list always contains a just-created delegated album
  // even when the active-catalog scoped fetch lags owner resolution.
  const fetchAllAccessibleCollections = useCallback(async () => {
    if (!isAuthenticated) return;
    setAllAccessibleLoading(true);
    try {
      const response = await api.get('/api/v2/collections?limit=100&scope=all');
      setAllAccessibleCollections(response.data);
      setAllAccessibleError(null);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load albums';
      // Surface the failure so the Albums page can show an error instead of a
      // misleading empty state; keep the last good union in place.
      setAllAccessibleError(errorMessage);
      logger.error('Error fetching all accessible collections', error);
    } finally {
      setAllAccessibleLoading(false);
    }
  }, [isAuthenticated]);

  const createCollection = useCallback(async (
    data: CollectionCreate,
    targetOwnerId?: string | null,
    targetCatalogAliId?: string | null,
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      // Resolve the owner the album is created FOR. `undefined` means "follow
      // the globally active catalog" (legacy behavior); an explicit value —
      // including `null` for the caller's own catalog — lets the Create Album
      // dialog target any catalog the user owns or fully delegates. When the
      // target is a delegator, the album lands in THAT delegator's catalog
      // (the backend verifies the caller holds a 'full' delegation).
      const resolvedOwnerId = targetOwnerId === undefined ? activeCatalogOwnerId : targetOwnerId;
      // Resolve the CATALOG the album is scoped to (`LL-NXX`). This is what
      // distinguishes two OWN catalogs that share an ownerId — without it the
      // album would leak into both. `undefined` follows the active catalog ALI.
      const resolvedAli = targetCatalogAliId === undefined ? activeCatalogAliId : targetCatalogAliId;
      const params = new URLSearchParams();
      if (resolvedOwnerId) params.append('owner_id', resolvedOwnerId);
      if (resolvedAli) params.append('catalog_ali_id', resolvedAli);
      const qs = params.toString();
      const url = qs ? `/api/v2/collections?${qs}` : '/api/v2/collections';
      const response = await api.post(url, data);
      // The backend persists ownerAliId, but fall back to the resolved ALI so the
      // optimistic entries carry their catalog immediately (no refetch gap).
      const newCollection = { ownerAliId: resolvedAli ?? null, ...response.data };

      // Only fold the new album into the currently-visible list (and auto-select
      // it) when it belongs to the pool on screen. Albums are owner-keyed, so the
      // "pool" is the owner id (null = own). When the target pool differs (e.g.
      // created in a delegated catalog while viewing own), the caller switches
      // the active catalog — which refetches the correct list — so appending
      // here would leak a foreign album into the wrong catalog's view.
      // The new album is always accessible to the caller, so fold it into the
      // cross-catalog union immediately (the Albums page reads this). A new
      // album the caller created in their OWN catalog keeps role 'owner'; one
      // created in a delegator's catalog is theirs-by-delegation, so clear the
      // role to match the scope=all contract (the caller is not the owner).
      const unionEntry: CollectionWithRole = {
        ...newCollection,
        role: resolvedOwnerId ? null : 'owner',
      };
      setAllAccessibleCollections(prev =>
        prev.some(c => c.collectionId === unionEntry.collectionId) ? prev : [...prev, unionEntry],
      );

      // Same pool = same owner AND same catalog. The catalog check matters for a
      // user with two OWN catalogs (shared ownerId): an album made for catalog A
      // must not be appended to catalog B's scoped list while B is on screen.
      const samePool =
        (resolvedOwnerId ?? null) === (activeCatalogOwnerId ?? null) &&
        (resolvedAli ?? null) === (activeCatalogAliId ?? null);
      if (samePool) {
        setCollections(prev => [...prev, newCollection]);
        setSelectedCollectionId(newCollection.collectionId);
        localStorage.setItem('selectedCollectionId', newCollection.collectionId);
      } else {
        // Different pool: the caller switches the active catalog right after, so
        // stash the new id for the catalog-switch reset effect to select once
        // the target catalog's albums load.
        pendingSelectAfterSwitchRef.current = newCollection.collectionId;
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create collection';
      setError(errorMessage);
      logger.error('Error creating collection', error);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [activeCatalogOwnerId, activeCatalogAliId]);

  const selectCollection = useCallback((collectionId: string | null) => {
    setSelectedCollectionId(collectionId);
    if (collectionId) {
      localStorage.setItem('selectedCollectionId', collectionId);
    } else {
      localStorage.removeItem('selectedCollectionId');
    }
  }, []);

  // Single Collection Operations
  const fetchCollectionDetail = useCallback(async (collectionId: string): Promise<CollectionWithRole> => {
    const response = await api.get(`/api/v2/collections/${collectionId}`);
    return response.data;
  }, []);

  const updateCollection = useCallback(async (collectionId: string, data: CollectionUpdate) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.put(`/api/v2/collections/${collectionId}`, data);
      const updatedCollection = response.data;

      // Update in local state (both the active-catalog list and the union)
      const patch = (c: CollectionWithRole) =>
        c.collectionId === collectionId ? { ...c, ...updatedCollection } : c;
      setCollections(prev => prev.map(patch));
      setAllAccessibleCollections(prev => prev.map(patch));
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update collection';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const deleteCollection = useCallback(async (collectionId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await api.delete(`/api/v2/collections/${collectionId}`);

      // Remove from local state (both the active-catalog list and the union)
      setCollections(prev => prev.filter(c => c.collectionId !== collectionId));
      setAllAccessibleCollections(prev => prev.filter(c => c.collectionId !== collectionId));

      // If this was the selected collection, clear selection
      if (selectedCollectionId === collectionId) {
        setSelectedCollectionId(null);
        localStorage.removeItem('selectedCollectionId');
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete collection';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [selectedCollectionId]);

  // Member Management
  const fetchMembers = useCallback(async (collectionId: string): Promise<CollectionMemberResponse[]> => {
    const response = await api.get(`/api/v2/collections/${collectionId}/members`);
    return response.data;
  }, []);

  const inviteMember = useCallback(async (collectionId: string, email: string, role: 'editor' | 'viewer') => {
    await api.post(`/api/v2/collections/${collectionId}/members`, { email, role });
  }, []);

  const updateMemberRole = useCallback(async (collectionId: string, userId: string, role: 'editor' | 'viewer') => {
    await api.put(`/api/v2/collections/${collectionId}/members/${userId}`, { role });
  }, []);

  const removeMember = useCallback(async (collectionId: string, userId: string) => {
    await api.delete(`/api/v2/collections/${collectionId}/members/${userId}`);
  }, []);

  // Invitations
  const fetchInvitations = useCallback(async (): Promise<PendingInvitation[]> => {
    const response = await api.get('/api/v2/invitations');
    return response.data;
  }, []);

  const acceptInvitation = useCallback(async (collectionId: string) => {
    await api.post(`/api/v2/invitations/${collectionId}/accept`);
    // Refresh both the active-catalog list and the cross-catalog union after
    // accepting (the newly-joined album is now accessible).
    await Promise.all([fetchCollections(), fetchAllAccessibleCollections()]);
  }, [fetchCollections, fetchAllAccessibleCollections]);

  const declineInvitation = useCallback(async (collectionId: string) => {
    await api.post(`/api/v2/invitations/${collectionId}/decline`);
  }, []);

  // Item Operations (Phase 8.8)
  // GH#1180: all three item-mutation / item-read helpers forward
  // activeCatalogOwnerId as ?owner_id= so that a full catalog delegate can
  // add/remove items on the delegator's albums.  Mirrors fetchCollections
  // which already passes the active owner id for the same reason (#958).
  const fetchCollectionItems = useCallback(async (collectionId: string): Promise<CollectionItemMembership[]> => {
    const params = new URLSearchParams();
    if (activeCatalogOwnerId) params.append('owner_id', activeCatalogOwnerId);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`/api/v2/collections/${collectionId}/items${query}`);
    return response.data;
  }, [activeCatalogOwnerId]);

  const addItemToCollection = useCallback(async (collectionId: string, catalogItemId: string, notes?: string) => {
    const params = new URLSearchParams();
    if (activeCatalogOwnerId) params.append('owner_id', activeCatalogOwnerId);
    const query = params.toString() ? `?${params.toString()}` : '';
    await api.post(`/api/v2/collections/${collectionId}/items${query}`, {
      catalog_item_id: catalogItemId,
      notes
    });
    await fetchCollections(); // Refresh to update item counts
    setMembershipVersion(v => v + 1); // GH#1213: signal membership changed
  }, [activeCatalogOwnerId, fetchCollections]);

  const removeItemFromCollection = useCallback(async (collectionId: string, itemId: string) => {
    const params = new URLSearchParams();
    if (activeCatalogOwnerId) params.append('owner_id', activeCatalogOwnerId);
    const query = params.toString() ? `?${params.toString()}` : '';
    await api.delete(`/api/v2/collections/${collectionId}/items/${itemId}${query}`);
    await fetchCollections();
    setMembershipVersion(v => v + 1); // GH#1213: signal membership changed
  }, [activeCatalogOwnerId, fetchCollections]);

  const fetchItemCollections = useCallback(async (itemId: string): Promise<ItemCollectionInfo[]> => {
    const params = new URLSearchParams();
    if (activeCatalogOwnerId) params.append('owner_id', activeCatalogOwnerId);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`/api/v2/items/${itemId}/collections${query}`);
    return response.data;
  }, [activeCatalogOwnerId]);

  // Load collections after authentication, passing activeCatalogOwnerId so
  // delegates fetch the delegator's collections instead of their own.
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchCollections(activeCatalogOwnerId, activeCatalogAliId);
    }
  }, [isAuthenticated, authLoading, fetchCollections, activeCatalogOwnerId, activeCatalogAliId]);

  // Load the cross-catalog union once after authentication. It is independent
  // of the active catalog (no owner/ali deps), so switching catalogs does not
  // refetch it — mutations keep it in sync incrementally.
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchAllAccessibleCollections();
    }
  }, [isAuthenticated, authLoading, fetchAllAccessibleCollections]);

  // Resolve the owner of the catalog currently being viewed. We deliberately do
  // NOT trust `activeCatalogOwnerId` alone: it can lag (resolved asynchronously
  // after the delegations fetch) or desync from `activeCatalogAliId` (a stale
  // switch can persist owner='__own__' while the ALI points at a delegated
  // catalog), which is what made a delegate's own albums leak into a delegated
  // catalog's per-object dropdown. `availableCatalogs` is the authoritative
  // ALI -> ownerUserId map; own catalogs map to null there, so we fall back to
  // the caller's own user id.
  const activeCatalogOwnerResolved = useMemo(() => {
    const fromAli = activeCatalogAliId
      ? availableCatalogs.find(c => c.catalogAliId === activeCatalogAliId)?.ownerUserId ?? null
      : null;
    return activeCatalogOwnerId ?? fromAli ?? ownUserId;
  }, [activeCatalogOwnerId, activeCatalogAliId, availableCatalogs, ownUserId]);

  // GH#1233: select an album from the cross-catalog Albums page. The union
  // spans every accessible catalog, but /my-objects renders the ACTIVE one —
  // so selecting a foreign-catalog album must also switch the active catalog
  // or the album's name would sit over the wrong catalog's items. Same-catalog
  // albums select directly; foreign ones stage the id in the pending slot
  // (which the catalog-switch reset effect consumes, exactly like cross-pool
  // create) and switch to the album's catalog.
  const selectCollectionCrossCatalog = useCallback(
    (collectionId: string) => {
      const target = allAccessibleCollections.find(c => c.collectionId === collectionId);
      if (!target) {
        selectCollection(collectionId);
        return;
      }
      const targetOwner = target.ownerId ?? ownUserId;
      const sameOwner = targetOwner === activeCatalogOwnerResolved;
      // ALI is the precise gate (distinguishes two OWN catalogs); fall back to
      // owner-only when either side is unknown, mirroring activeCatalogCollections.
      const sameCatalog =
        sameOwner &&
        (!activeCatalogAliId || !target.ownerAliId || target.ownerAliId === activeCatalogAliId);
      if (sameCatalog) {
        selectCollection(collectionId);
        return;
      }
      // Albums predating per-catalog scoping carry no ownerAliId — resolve the
      // delegator's catalog through the authoritative owner -> ALI map. Own
      // albums resolve to null, which switchToCatalogAliId maps to the primary.
      const targetAli =
        target.ownerAliId ??
        (targetOwner === ownUserId
          ? null
          : availableCatalogs.find(c => c.ownerUserId === targetOwner)?.catalogAliId ?? null);
      if (targetAli === null && targetOwner !== ownUserId) {
        // Delegator catalog not resolvable yet (catalog list still loading):
        // a null switch would land on the OWN catalog, which is worse than the
        // plain select we ship today — keep the legacy behavior for this race.
        selectCollection(collectionId);
        return;
      }
      pendingSelectAfterSwitchRef.current = collectionId;
      switchToCatalogAliId(targetAli);
    },
    [
      allAccessibleCollections,
      selectCollection,
      ownUserId,
      activeCatalogOwnerResolved,
      activeCatalogAliId,
      availableCatalogs,
      switchToCatalogAliId,
    ],
  );

  // Albums for the catalog being viewed, for per-object album pickers. Sourced
  // from the union (so a just-created delegated album is always present) merged
  // with the active-catalog scoped list (so a view-only delegate — absent from
  // the full-only union — still sees the active catalog's albums), then filtered
  // to the resolved active owner so foreign-catalog albums never appear.
  const activeCatalogCollections = useMemo(() => {
    if (!activeCatalogOwnerResolved) return collections;
    const byId = new Map<string, CollectionWithRole>();
    // Active-catalog list first so its (correct, per-active-catalog) role wins
    // over the union's role-cleared delegated entries.
    for (const c of collections) byId.set(c.collectionId, c);
    for (const c of allAccessibleCollections) {
      if (!byId.has(c.collectionId)) byId.set(c.collectionId, c);
    }
    return Array.from(byId.values()).filter(c => {
      // Owner is the coarse gate — never show another owner's album.
      if (c.ownerId !== activeCatalogOwnerResolved) return false;
      // Catalog ALI is the precise gate: it's the ONLY thing that distinguishes
      // two catalogs the same user owns. When both the active ALI and the
      // album's ownerAliId are known, require an exact match so an album created
      // on one own catalog doesn't leak into the other's per-object picker.
      if (activeCatalogAliId && c.ownerAliId) return c.ownerAliId === activeCatalogAliId;
      // Fall back to owner-only while the ALI is unresolved or the album predates
      // per-catalog scoping (legacy NULL ownerAliId) — preserves prior behavior.
      return true;
    });
  }, [collections, allAccessibleCollections, activeCatalogOwnerResolved, activeCatalogAliId]);

  // Validate and restore selected collection after collections load (runs only once on initialization)
  useEffect(() => {
    // Only run initialization once and when collections are loaded
    if (collections.length > 0 && !isInitialized.current) {
      const savedCollectionId = localStorage.getItem('selectedCollectionId');

      // Validate that the saved collection ID still exists
      if (savedCollectionId) {
        const collectionExists = collections.some(c => c.collectionId === savedCollectionId);
        if (collectionExists) {
          setSelectedCollectionId(savedCollectionId);
          isInitialized.current = true;
          return;
        } else {
          // Stale collection ID - remove from localStorage
          localStorage.removeItem('selectedCollectionId');
        }
      }

      // No valid saved collection - default to "All Items" (null)
      setSelectedCollectionId(null);
      localStorage.removeItem('selectedCollectionId');
      isInitialized.current = true;
    }
  }, [collections]);

  return (
    <>
      <CollectionsContext.Provider
        value={{
          collections,
          allAccessibleCollections,
          allAccessibleLoading,
          allAccessibleError,
          activeCatalogCollections,
          selectedCollectionId,
          isLoading,
          error,
          fetchCollections,
          createCollection,
          selectCollection,
          selectCollectionCrossCatalog,
          fetchCollectionDetail,
          updateCollection,
          deleteCollection,
          fetchMembers,
          inviteMember,
          updateMemberRole,
          removeMember,
          fetchInvitations,
          acceptInvitation,
          declineInvitation,
          fetchCollectionItems,
          addItemToCollection,
          removeItemFromCollection,
          fetchItemCollections,
          membershipVersion
        }}
      >
        {children}
      </CollectionsContext.Provider>

      {/* Error notification */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setSnackbarOpen(false)}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export { CollectionsProvider };
